import React from 'react';
import { cn } from 'utils/helpers';
import loaderSvg from 'assets/images/loader.svg';

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  color?: 'primary' | 'secondary' | 'white';
  useCustomLoader?: boolean;
}

export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  className,
  color = 'primary',
  useCustomLoader = true,
}) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  const colors = {
    primary: 'text-primary-600',
    secondary: 'text-secondary-600',
    white: 'text-white',
  };

  if (useCustomLoader) {
    return (
      <div className="flex items-center justify-center">
        <img
          src={loaderSvg}
          alt="Loading..."
          className={cn('animate-spin-smooth transition-all duration-300 ease-in-out', sizes[size], className)}
        />
      </div>
    );
  }

  // Enhanced SVG spinner with smoother animation
  return (
    <div className="flex items-center justify-center">
      <svg
        className={cn('animate-spin-smooth transition-all duration-300 ease-in-out', sizes[size], colors[color], className)}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-20"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
        ></circle>
        <path
          className="opacity-80"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>
  );
};

interface LoadingOverlayProps {
  message?: string;
  showSpinner?: boolean;
  withBackground?: boolean;
  withBlur?: boolean;
  effect?: 'blur' | 'fade' | 'glass' | 'minimal' | 'elegant' | 'soft' | 'premium' | 'gentle' | 'modern' | 'solid';
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  message = 'Loading...',
  showSpinner = true,
  withBackground = false,
  withBlur = true,
  effect = 'elegant',
}) => {
  const getEffectClasses = () => {
    switch (effect) {
      case 'solid':
        return 'bg-white';
      case 'blur':
        return 'backdrop-blur-[2px] bg-secondary-50/25 dark:bg-secondary-900/25';
      case 'fade':
        return 'bg-secondary-900/30 dark:bg-secondary-900/40';
      case 'glass':
        return 'backdrop-blur-[8px] bg-secondary-50/20 dark:bg-secondary-900/25';
      case 'minimal':
        return 'bg-transparent';
      case 'elegant':
        return 'backdrop-blur-[3px] bg-gradient-to-br from-secondary-50/15 via-secondary-100/20 to-secondary-50/15 dark:from-secondary-800/20 dark:via-secondary-700/25 dark:to-secondary-800/20';
      case 'soft':
        return 'backdrop-blur-[1px] bg-secondary-50/20 dark:bg-secondary-900/25';
      case 'premium':
        return 'backdrop-blur-[4px] bg-gradient-to-br from-primary-50/20 via-secondary-50/15 to-primary-100/20 dark:from-primary-900/25 dark:via-secondary-800/20 dark:to-primary-800/25';
      case 'gentle':
        return 'backdrop-blur-[0.5px] bg-secondary-50/15 dark:bg-secondary-900/20';
      case 'modern':
        return 'backdrop-blur-[6px] bg-gradient-to-br from-secondary-50/18 via-secondary-100/22 to-secondary-50/18 dark:from-secondary-800/25 dark:via-secondary-700/30 dark:to-secondary-800/25';
      default:
        return 'backdrop-blur-[3px] bg-secondary-50/20 dark:bg-secondary-900/25';
    }
  };

  const getContainerClasses = () => {
    switch (effect) {
      case 'solid':
        return '';
      case 'glass':
        return 'bg-transparent dark:bg-transparent backdrop-blur-[12px] rounded-3xl p-10 shadow-2xl border border-white/20 dark:border-secondary-600/20';
      case 'elegant':
        return withBackground ? 'bg-transparent dark:bg-transparent backdrop-blur-[6px] rounded-2xl p-8 shadow-2xl border border-secondary-200/30 dark:border-secondary-600/30' : '';
      case 'minimal':
        return '';
      case 'soft':
        return withBackground ? 'bg-transparent dark:bg-transparent backdrop-blur-[4px] rounded-xl p-6 shadow-lg border border-secondary-200/20 dark:border-secondary-600/20' : '';
      case 'premium':
        return withBackground ? 'bg-transparent dark:bg-transparent backdrop-blur-[8px] rounded-2xl p-8 shadow-2xl border border-primary-200/30 dark:border-primary-600/30' : '';
      case 'gentle':
        return withBackground ? 'bg-transparent dark:bg-transparent backdrop-blur-[2px] rounded-lg p-4 shadow-md border border-secondary-200/15 dark:border-secondary-600/15' : '';
      case 'modern':
        return withBackground ? 'bg-transparent dark:bg-transparent backdrop-blur-[10px] rounded-2xl p-8 shadow-xl border border-secondary-300/25 dark:border-secondary-500/25' : '';
      default:
        return withBackground ? 'bg-transparent dark:bg-transparent backdrop-blur-[4px] rounded-xl p-8 shadow-xl border border-secondary-200/25 dark:border-secondary-600/25' : '';
    }
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center z-50 animate-fade-in ${getEffectClasses()}`}>
      <div className={`flex flex-col items-center gap-6 animate-scale-in ${getContainerClasses()}`}>
        {showSpinner && (
          <div className="relative">
            <Spinner size="lg" />
            {/* Subtle pulsing ring for extra visual appeal */}
            <div className="absolute inset-0 rounded-full border-2 border-primary-300/40 dark:border-primary-500/40 animate-pulse-soft"></div>
          </div>
        )}
        <p className="text-secondary-700 dark:text-secondary-200 font-medium text-center text-lg leading-relaxed max-w-sm px-4 animate-fade-in">
          {message}
        </p>
      </div>
    </div>
  );
};

interface LoadingButtonProps {
  isLoading: boolean;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  loadingText?: string;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  isLoading,
  children,
  className = '',
  disabled = false,
  onClick,
  loadingText,
}) => {
  return (
    <button
      className={`relative ${className}`}
      disabled={disabled || isLoading}
      onClick={onClick}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner size="sm" color="white" />
        </div>
      )}
      <span className={isLoading ? 'opacity-0' : 'opacity-100'}>
        {isLoading && loadingText ? loadingText : children}
      </span>
    </button>
  );
};

interface LoadingInlineProps {
  isLoading: boolean;
  children: React.ReactNode;
  message?: string;
  className?: string;
}

export const LoadingInline: React.FC<LoadingInlineProps> = ({
  isLoading,
  children,
  message = 'Loading...',
  className = '',
}) => {
  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Spinner size="sm" />
        <span className="text-sm text-gray-600 dark:text-gray-400">{message}</span>
      </div>
    );
  }

  return <>{children}</>;
};

